import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import { DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX } from "@src/helpers/regex";
import useZustandStore from "@src/helpers/zustand";
import { RadioGroup, RadioGroupItem } from "@src/components/ui/radio-group";
import { cn } from "@src/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/components/ui/select";
import { Textarea } from "@src/components/ui/textarea";
import { useLegalCustomerEmploymentTypes } from "@src/api/useLegalCustomerEmploymentTypes";
import { useEntrepreneurWorkTypes } from "@src/api/useEntrepreneurWorkTypes";

const FormSchema = z
  .object({
    bussinessType: z.string().refine((v) => v),
    entrepreneurDetailsOther: z.string().optional(),
    entrepreneurDetails: z.string().optional(),
    legalName: z.string().optional(),
    legalIdentificationNumber: z.string().optional(),
    legalWorkTypeOther: z.string().optional(),
    legalWorkType: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.bussinessType === "legal") {
      if (!data.legalName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["legalName"], // Path points to the field with the issue
        });
      }

      if (!data.legalIdentificationNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["legalIdentificationNumber"],
        });
      }

      if (!data.legalWorkType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["legalWorkType"],
        });
      }
      if (data.legalWorkType === "Other" && !data.legalWorkTypeOther) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "",
          path: ["legalWorkTypeOther"],
        });
      }
    } else if (!data.entrepreneurDetails) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "",
        path: ["entrepreneurDetails"],
      });
    }
    if (
      data.entrepreneurDetails === "Other" &&
      !data.entrepreneurDetailsOther
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "",
        path: ["entrepreneurDetailsOther"],
      });
    }
  });

function BusinessDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);
  const legalCustomerEmploymentTypes = useLegalCustomerEmploymentTypes();
  // const entrepreneurWorkTypes = useEntrepreneurWorkTypes();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      bussinessType: zData.employment.bussinessType,
      entrepreneurDetailsOther: zData.employment.entrepreneurDetailsOther,
      entrepreneurDetails: zData.employment.entrepreneurDetails,
      legalName: zData.employment.legalName,
      legalIdentificationNumber: zData.employment.legalIdentificationNumber,
      legalWorkType: zData.employment.legalName,
      legalWorkTypeOther: zData.employment.legalWorkTypeOther,
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      employment: {
        ...zData.employment,
        bussinessType: data.bussinessType,
        entrepreneurDetailsOther: data.entrepreneurDetailsOther ?? "",
        entrepreneurDetails: data.entrepreneurDetails ?? "",
        legalName: data.legalName ?? "",
        legalIdentificationNumber: data.legalIdentificationNumber ?? "",
        legalWorkType: data.legalWorkType ?? "",
        legalWorkTypeOther: data.legalWorkTypeOther ?? "",
      },
    });
    navigate(`/${i18n.language}/source-of-income`);
  }

  const [bussinessType, legalWorkType, entrepreneurDetails] = form.watch([
    "bussinessType",
    "legalWorkType",
    "entrepreneurDetails",
  ]);

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc
            desc={t("employment.business.desc")}
            title={t("employment.business.title")}
          />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="bussinessType"
            render={({ field }) => {
              return (
                <FormItem>
                  <RadioGroup
                    className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                    onValueChange={(e) => {
                      field.onChange(e);
                      form.setValue("entrepreneurDetails", "");
                      form.setValue("legalName", "");
                      form.setValue("legalIdentificationNumber", "");
                      form.setValue("legalWorkType", "");
                      form.setValue("legalWorkTypeOther", "");
                      form.setValue("entrepreneurDetailsOther", "");
                    }}
                    value={field.value}
                  >
                    {["entrepreneur", "legal"].map((item) => (
                      <FormItem
                        className={cn(
                          "flex items-center justify-between border-b border-solid border-gray-200",
                          "last:border-b-0 relative"
                        )}
                        key={item}
                      >
                        <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                          {t(`employment.business.checks.${item}`)}
                        </FormLabel>
                        <FormControl className="!mt-0 right-4 absolute">
                          <RadioGroupItem value={item} />
                        </FormControl>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormItem>
              );
            }}
          />
          <div className="flex flex-col gap-2 mt-2">
            {bussinessType === "entrepreneur" && (
              <>
                <FormField
                  control={form.control}
                  name="entrepreneurDetails"
                  render={({ field }) => {
                    return (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className={cn(
                            legalWorkType === "Other" &&
                              "w-full text-base border-whitebitBlack"
                          )}
                          placeholder={t(
                            "employment.business.inputs.legalWorkType"
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent side="bottom">
                          {legalCustomerEmploymentTypes?.data?.data?.legalCustomerEmploymentTypes.map(
                            (el) => (
                              <SelectItem
                                className="text-sm p-1 cursor-pointer"
                                key={el.legalEmploymentTypeCode}
                                value={el.legalEmploymentTypeCode}
                              >
                                {el.legalEmploymentType}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                    );
                  }}
                />

                {entrepreneurDetails === "Other" && (
                  <FormField
                    control={form.control}
                    name="entrepreneurDetailsOther"
                    render={({ field }) => (
                      <FormItem>
                        <Textarea
                          {...field}
                          className="rounded-lg mt-2 bg-white"
                          onChange={(e) => {
                            const { target } = e;
                            if (
                              DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                                target.value
                              ) ||
                              !target.value
                            ) {
                              field.onChange(e);
                            }
                          }}
                          placeholder={t(
                            "employment.business.inputs.entrepreneurDetails"
                          )}
                        />
                      </FormItem>
                    )}
                  />
                )}
              </>
            )}
            {bussinessType === "legal" && (
              <>
                <FormField
                  control={form.control}
                  name="legalName"
                  render={({ field }) => (
                    <FormItem>
                      <Input
                        {...field}
                        label={t("employment.business.inputs.legalName")}
                        onChange={(e) => {
                          const { target } = e;
                          if (
                            DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                              target.value
                            ) ||
                            !target.value
                          ) {
                            field.onChange(e);
                          }
                        }}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="legalIdentificationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <Input
                        {...field}
                        label={t(
                          "employment.business.inputs.legalIdentificationNumber"
                        )}
                        onChange={(e) => {
                          const { target } = e;
                          if (
                            DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                              target.value
                            ) ||
                            !target.value
                          ) {
                            field.onChange(e);
                          }
                        }}
                      />
                    </FormItem>
                  )}
                />
                <div
                  className={cn(
                    legalWorkType === "Other" &&
                      "border border-solid border-whitebitBlack/5 p-2 rounded-lg"
                  )}
                >
                  <FormField
                    control={form.control}
                    name="legalWorkType"
                    render={({ field }) => {
                      return (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            className={cn(
                              legalWorkType === "Other" && "w-full"
                            )}
                            placeholder={t(
                              "employment.business.inputs.legalWorkType"
                            )}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent side="bottom">
                            {legalCustomerEmploymentTypes?.data?.data?.legalCustomerEmploymentTypes.map(
                              (el) => (
                                <SelectItem
                                  className="text-sm p-1 cursor-pointer"
                                  key={el.legalEmploymentTypeCode}
                                  value={el.legalEmploymentTypeCode}
                                >
                                  {el.legalEmploymentType}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      );
                    }}
                  />

                  {legalWorkType === "Other" && (
                    <FormField
                      control={form.control}
                      name="legalWorkTypeOther"
                      render={({ field }) => (
                        <FormItem>
                          <Textarea
                            {...field}
                            className="rounded-lg mt-2 bg-white"
                            onChange={(e) => {
                              const { target } = e;
                              if (
                                DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                                  target.value
                                ) ||
                                !target.value
                              ) {
                                field.onChange(e);
                              }
                            }}
                            placeholder={t("employment.business.title")}
                          />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default BusinessDetails;
